import { AVAILABLE_SERVICE, CASE_STATUS_STEP, Consultation, CONSULTATION_STEPS, MODULE_TYPE, Question, STEP_TYPE } from '@abiglobalhealth/go-types'
import * as Sentry from '@sentry/browser'
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useEffect, useState } from 'react'

import { checkConsultationLimitExceeded } from '@/api/consultations'
import { useConfig } from '@/hooks/useConfig'
import { useConsultationEnded } from '@/hooks/useConsultationEnded'
import { useJourney } from '@/hooks/useJourney'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useUser } from '@/hooks/useUser'
import { isConsultationEnded } from '@/utils/consultation'
import { isNavigatorModule } from '@/utils/navigator'

export type Attachment = {
  file: File
  preview: string | ArrayBuffer | null
  url: string
}

export interface ConsultationContextProps {
  currentConsultationStep: CONSULTATION_STEPS | null
  setCurrentConsultationStep: Dispatch<SetStateAction<CONSULTATION_STEPS | null>>
  consultation: Consultation | null
  setConsultation: Dispatch<SetStateAction<Consultation | null>>
  currentQuestion: Question
  setCurrentQuestion: Dispatch<SetStateAction<Question>>
  attachments: Attachment[]
  setAttachments: Dispatch<SetStateAction<Attachment[]>>
  consultationType: AVAILABLE_SERVICE
  updatingConsultationType: boolean
  setUpdatingConsultationType: Dispatch<SetStateAction<boolean>>
  limitExceeded: boolean
  setLimitExceeded: Dispatch<SetStateAction<boolean>>
  checkLimitExceeded: (type?: AVAILABLE_SERVICE) => Promise<boolean>
  setConsultationType: Dispatch<SetStateAction<AVAILABLE_SERVICE>>
}

export const ConsultationContext = createContext({} as ConsultationContextProps)

export const ConsultationProvider = ({ children }: PropsWithChildren) => {
  const [limitExceeded, setLimitExceeded] = useState(false)
  const { user } = useUser()
  const { initialized } = useJourney()
  const { updateStep, updateFulfillmentStep, currentStep } = useStep()
  const { currentModule } = useModule()
  const { config } = useConfig()
  const consultationEnded = useConsultationEnded()

  const [currentConsultationStep, setCurrentConsultationStep] = useState<ConsultationContextProps['currentConsultationStep']>(null)
  const [consultation, setConsultation] = useState<ConsultationContextProps['consultation']>(null)
  const [currentQuestion, setCurrentQuestion] = useState<ConsultationContextProps['currentQuestion']>({
    question: '',
    attachments: []
  })
  const queryParams = new URLSearchParams(window.location.search)
  const [consultationType, setConsultationType] = useState<AVAILABLE_SERVICE>(
    (queryParams.get('q') as AVAILABLE_SERVICE) || config?.consultation?.availableServices[0]
  )
  const [updatingConsultationType, setUpdatingConsultationType] = useState(false)

  const [attachments, setAttachments] = useState<Attachment[]>([])

  const setLandingStep = useCallback(() => {
    if (currentModule?.type === MODULE_TYPE.DEFAULT && currentStep?.type === STEP_TYPE.USER_FORM) {
      setCurrentConsultationStep(CONSULTATION_STEPS.FORM)
      return
    }

    if (isNavigatorModule(currentModule)) return

    setCurrentConsultationStep(CONSULTATION_STEPS.LANDING)
    updateStep({
      id: `consultation-${CONSULTATION_STEPS.LANDING}`,
      components: {},
      name: `consultation-${CONSULTATION_STEPS.LANDING}`,
      componentsOrder: [],
      type: STEP_TYPE.CONSULTATION
    })
  }, [currentStep, currentModule])

  const checkLimitExceeded = (type?: AVAILABLE_SERVICE) =>
    checkConsultationLimitExceeded({ consultationType: type || consultationType, uniqueId: user?.data.uniqueId! }).then((res) => {
      setLimitExceeded(res)
      return res
    })

  useEffect(() => {
    setCurrentQuestion((prev) => ({
      ...prev,
      attachments: attachments.map((attachment) => attachment.url)
    }))
  }, [attachments])

  useEffect(() => {
    if (!consultation) return setLandingStep()

    Sentry.setContext('Consultation', {
      id: consultation.consultationId,
      status: consultation.status,
      type: consultation.consultationType,
      createdAt: consultation.createdAt
    })

    Sentry.setTag('consultationId', consultation.consultationId)
    if (!isConsultationEnded(consultation)) {
      setConsultationType(consultation.consultationType)
    }
    if (isConsultationEnded(consultation) && isNavigatorModule(currentModule)) {
      return
    }
    setCurrentConsultationStep(CASE_STATUS_STEP[consultation.status] || CONSULTATION_STEPS.LANDING)
  }, [consultation])

  useEffect(() => {
    if (!currentModule || !('consultationType' in currentModule)) return

    if (consultationEnded || isNavigatorModule(currentModule)) {
      setConsultationType(currentModule?.consultationType as AVAILABLE_SERVICE)
    }

    if (isNavigatorModule(currentModule)) return

    switch (currentModule?.type) {
      case MODULE_TYPE.CONSULTATION:
      case MODULE_TYPE.CONSULTATION_LANDING:
      case MODULE_TYPE.ASSESSMENT_SURVEY_CATEGORIES:
      case MODULE_TYPE.INDICATION_CATEGORIES: {
        setLandingStep()
        break
      }

      case MODULE_TYPE.INDICATION_SURVEY: {
        setCurrentConsultationStep(CONSULTATION_STEPS.INDICATION_SURVEY)
        break
      }
    }
  }, [currentModule])

  useEffect(() => {
    if (!currentModule) return

    switch (currentConsultationStep) {
      case CONSULTATION_STEPS.WAITING_FOR_INFO:
        updateFulfillmentStep({
          option: consultation?.prescription?.fulfillmentOptions?.length === 1 ? consultation.prescription.fulfillmentOptions[0] : null
        })
        break

      case CONSULTATION_STEPS.LANDING:
        if (currentModule?.type !== MODULE_TYPE.INDICATION_SURVEY) setLandingStep()

        break
    }
  }, [currentConsultationStep, currentModule])

  useEffect(() => {
    Sentry.setContext('Consultation info', { consultationType, currentConsultationStep })
  }, [consultationType, currentConsultationStep])

  useEffect(() => {
    // Only first time (getInitialModule)
    if (!user?.data?.uniqueId || initialized) return

    checkLimitExceeded(consultationType)
  }, [consultationType, user?.data.uniqueId, initialized])

  const value = {
    currentConsultationStep,
    setCurrentConsultationStep,
    consultation,
    setConsultation,
    currentQuestion,
    setCurrentQuestion,
    attachments,
    setAttachments,
    consultationType,
    updatingConsultationType,
    setUpdatingConsultationType,
    limitExceeded,
    setLimitExceeded,
    checkLimitExceeded,
    setConsultationType
  }

  return <ConsultationContext.Provider value={value}>{children}</ConsultationContext.Provider>
}
